import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The Transform Better e-book's own page.
 *
 * Rebuilt to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/transform-better-e-book/`), which Kagan sent as seven
 * screenshots on 2026-08-26 with "yapabildiğin kadar birebir aynısını yap". So
 * the structure here is that page's, section for section: the cover as a dark
 * band, one centred standfirst with the download under it, the gradient
 * "5 BEST PRACTICES" divider, then the five practices alternating side to side,
 * and the offer again in a card at the end. The copy is that page's copy.
 *
 * Every button goes to the same PDF. That is the reference's behaviour and it
 * is deliberate rather than lazy: the reader who is convinced by the third
 * argument should not have to scroll back to the top to act on it.
 *
 * The artwork and the five photographs ARE the reference's own, since
 * 2026-08-28: Kagan sent the page's images alongside the screenshots
 * ("bir görselin senin elinde daha uygun daha kaliteli hali varsa onu tercih
 * et"), which retired the substitutes an earlier session had chosen from the
 * site's own photography. The cover photograph — the mountain road at dusk with
 * the light trails — arrived on 2026-08-30, this time without the lettering
 * burned into it, so the opening band is the reference's own picture under its
 * scrim rather than the site's plain navy.
 */
export const ebookTransformBetter: WrittenPage = {
  navLabel: 'Transform Better',
  title: 'Transform Better: the e-book',
  description:
    'Five pivotal learnings from accompanying hundreds of companies through digital transformation. Download the Transform Better e-book.',
  sections: [
    {
      kind: 'cover',
      lines: ['Transform', 'Better'],
      sub: 'Transformation success is possible.',
      photo: {
        src: '/photo/transform-better-cover.webp',
        alt: 'A hairpin mountain road at dusk, the headlights of passing cars drawn into long ribbons of light through the forest.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      paragraphs: [
        'Find the keys to the thriving future of your business with our comprehensive guide to '
          + 'successful digital transformation, packed with invaluable insights gained as a result '
          + 'of Digitopia’s role in accompanying hundreds of companies through their digital '
          + 'transformations. Discover the five pivotal learnings that will empower you to '
          + 'seamlessly navigate the complex landscape of Digital Transformation. This e-book '
          + 'delves into proven strategies and real-world case studies that illuminate the path to '
          + 'success. Whether you’re a seasoned entrepreneur or a curious newcomer, our e-book '
          + 'equips you with the tools to harness your potential and revolutionize your '
          + 'organization.',
      ],
      cta: { label: 'Download Report', href: LINKS.transformBetterEbookPdf },
    },
    {
      kind: 'banner',
      lines: ['5 Best', 'Practices'],
      photo: {
        src: '/photo/transform-better-five-best-practices.webp',
        alt: '5 Best Practices: a three-dimensional glass numeral 5 standing over a blue-to-pink gradient card.',
      },
    },
    {
      kind: 'split',
      flip: true,
      heading: 'Transformation is a journey.',
      paragraphs: [
        'Digital Transformation is a perpetual journey that seeks to reshape organizations '
          + 'through technology integration, cultural shifts, and process enhancements. It’s not a '
          + 'destination, but a continuous evolution aimed at improving customer experiences, '
          + 'operational efficiency, and innovation. Embracing this journey ensures long-term '
          + 'growth and competitiveness in the digital age.',
      ],
      photo: {
        src: '/photo/sailing-boat-open-sea.webp',
        alt: 'A yacht under full sail on open water, seen from above.',
      },
      cta: { label: 'Download Report', href: LINKS.transformBetterEbookPdf },
    },
    {
      kind: 'split',
      heading: 'Transformation is a team sport.',
      paragraphs: [
        'Digital transformation thrives on collective effort. It necessitates collaboration '
          + 'across departments, from leadership envisioning strategies to IT implementing '
          + 'technologies. A unified team drives cultural change, adapting workflows to new tools '
          + 'and fostering innovation. Success lies in aligning diverse skills and perspectives '
          + 'towards a shared vision, propelling the organization into a digitally empowered '
          + 'future.',
      ],
      photo: {
        src: '/photo/volleyball-block-at-the-net.webp',
        alt: 'Two volleyball players reaching for the same ball above the net.',
      },
      cta: { label: 'Download Report', href: LINKS.transformBetterEbookPdf },
    },
    {
      kind: 'split',
      flip: true,
      heading: 'Transformation is a corporate matter.',
      paragraphs: [
        'Digital transformation transcends individual roles; it is a corporate imperative. It '
          + 'reshapes business models, requiring leadership commitment to drive cultural shifts '
          + 'and allocate resources. Every aspect, from customer interactions to operations, is '
          + 'affected. Embracing this holistic change equips the organization to thrive in the '
          + 'digital age and remain competitive.',
      ],
      photo: {
        src: '/photo/boardroom-applause-dashboards.webp',
        alt: 'A meeting room applauding a colleague who has just presented a wall of performance dashboards.',
      },
      cta: { label: 'Download Report', href: LINKS.transformBetterEbookPdf },
    },
    {
      kind: 'split',
      heading: 'Transformation requires focus.',
      paragraphs: [
        'Digital transformation demands unwavering focus. It involves strategic allocation of '
          + 'resources and prioritization of initiatives. Amid technological complexities, '
          + 'maintaining clarity on objectives ensures effective implementation and maximum '
          + 'impact. A concentrated approach propels the organization towards successful digital '
          + 'evolution. Informed decisions and precise execution mark the difference between '
          + 'winners and losers.',
      ],
      photo: {
        src: '/photo/woman-in-crowd-city-street.webp',
        alt: 'A woman looking steadily off to one side while a busy street blurs around her.',
      },
      cta: { label: 'Download Report', href: LINKS.transformBetterEbookPdf },
    },
    {
      kind: 'split',
      flip: true,
      heading: "What's measured gets done.",
      paragraphs: [
        'Metrics provide visibility into progress, revealing strengths and areas needing '
          + 'attention. Tracking key performance indicators (KPIs) ensures alignment with goals '
          + 'and empowers data-driven decision-making. This iterative process enhances '
          + 'accountability, accelerates implementation, and ultimately drives the successful '
          + 'realization of digital transformation initiatives.',
      ],
      photo: {
        src: '/photo/airliner-cockpit-checklist.webp',
        alt: 'Two pilots in an airliner cockpit reading a checklist against their instruments.',
      },
      cta: { label: 'Download Report', href: LINKS.transformBetterEbookPdf },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading:
        'Unlock success in the digital age. Grab your transformative guide now and #transformbetter!',
      ctas: [{ label: 'Download Report', href: LINKS.transformBetterEbookPdf }],
      photo: {
        src: '/photo/transform-better-ebook-covers.webp',
        alt: 'Four copies of the Transform Better e-book fanned out, the mountain road on the cover.',
      },
    },
  ],
}
